import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import LogicContext from "../../Context/LogicContext";
import { moduleListService } from "../../services/module-list.service";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import { GetModuleEdit, SetModuleEdit } from "../../store/moduleListAtom";

const AddNewModulePage = () => {
    const editableModule = GetModuleEdit();
    const setModuleEdit = SetModuleEdit();
    const myPromiseModal = createModal(GeneralModal);
    const navigate = useNavigate();
    const logicContext = useContext(LogicContext);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { pathname } = useLocation();
    const url = pathname.split("/");
    const currModuleId = url[1];
    const currModuleName = url[2];
    const currUserMenu = url[3];

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm({
        defaultValues: {
            Module_Name: "",
            Module_Description: "",
            Module_Status_Id: "A",
        },
    });

    // Clear the editable module state when the user leaves the page
    useEffect(() => {
        return () => {
            setModuleEdit({});
        };
    }, [setModuleEdit]);

    // Populate form for Edit or reset for Create
    useEffect(() => {
        if (editableModule?.module_id) {
            // Edit Mode
            reset({
                module_id: editableModule.module_id,
                Module_Name: editableModule.module_name,
                Module_Description: editableModule.module_description,
                Module_Status_Id: editableModule.status_id || "A",
            });
        } else {
            // Create Mode
            reset({
                Module_Name: "",
                Module_Description: "",
                Module_Status_Id: "A",
            });
        }
    }, [editableModule, reset]);

    const onSubmit = async (data) => {
        const confirmed = await myPromiseModal({
            title: editableModule?.module_id ? "Update Module" : "Save Module",
            body: editableModule?.module_id
                ? "Would you like to update this module?"
                : "Would you like to save this module?",
            showInput: false,
        });

        if (!confirmed?.decision) return;
        setIsSubmitting(true);

        try {
            const payload = {
                Module_Name: data.Module_Name,
                Module_Description: data.Module_Description,
                Module_Status_Id: data.Module_Status_Id,
                option: editableModule?.module_id ? 2 : 1,
            };

            if (editableModule?.module_id) {
                payload.module_id = editableModule.module_id;
            }

            const response = await moduleListService.createOrUpdateModule(payload);
            const result = logicContext.httpResponseManager(response.data?.[0] || response.data?.result_status?.[0]);

            if (result.message_id === 1) {
                logicContext.showToast(
                    result.message_text ||
                    (editableModule?.module_id ? 'Module updated successfully!' : 'Module created successfully!'),
                    'success'
                );

                sessionStorage.setItem('reloadPage', 'true');
                setModuleEdit({})
                navigate(-1);
            } else {
                logicContext.showToast(result.message_text || 'Operation failed', 'error');
            }
        } catch (error) {
            console.error("Error saving module:", error);
            logicContext.showToast('Failed to save module. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = async () => {
        const result = await myPromiseModal({
            title: 'Cancel',
            body: 'Are you sure you want to cancel? All unsaved changes will be lost.',
            showInput: false,
        });

        if (result.decision === true) {
            navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}`);
        }
    };

    return (
        <section className="mt-4 w-full mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {editableModule?.module_id ? "Edit Module" : "Add New Module"}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                    {editableModule?.module_id ? "Update subscription module" : "Create a new subscription module"}
                </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
                <div className="flex-1">
                    <div className="bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100">
                        {/* Header Banner */}
                        <div className="h-32 bg-gradient-to-r from-blue-600 to-blue-500 relative">
                            <div className="absolute -bottom-12 left-8 flex items-end gap-6">
                                <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center p-1">
                                    <div className="w-full h-full bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <h1 className="text-2xl font-bold text-white drop-shadow-md mb-4">
                                        {editableModule?.module_id ? "Edit Module" : "New Module"}
                                    </h1>
                                </div>
                            </div>
                        </div>

                        {/* Form Content */}
                        <form onSubmit={handleSubmit(onSubmit)} className="p-8 pt-16">
                            <div className="space-y-6">
                                {/* Module Name */}
                                <div>
                                    <label htmlFor="Module_Name" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Module Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="Module_Name"
                                        type="text"
                                        {...register("Module_Name", {
                                            required: "Module name is required",
                                            minLength: { value: 2, message: "Module name must be at least 2 characters" },
                                            maxLength: { value: 100, message: "Module name must not exceed 100 characters" },
                                        })}
                                        className={`w-full px-4 py-3 rounded-xl border ${errors.Module_Name ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"} focus:outline-none focus:ring-2 transition-all duration-200`}
                                        placeholder="Enter module name"
                                    />
                                    {errors.Module_Name && (
                                        <p className="mt-1.5 text-sm text-red-600">{errors.Module_Name.message}</p>
                                    )}
                                </div>

                                {/* Module Description */}
                                <div>
                                    <label htmlFor="Module_Description" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Module Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        id="Module_Description"
                                        {...register("Module_Description", {
                                            required: "Module description is required",
                                            minLength: { value: 5, message: "Description must be at least 5 characters" },
                                            maxLength: { value: 500, message: "Description must not exceed 500 characters" },
                                        })}
                                        rows={4}
                                        className={`w-full px-4 py-3 rounded-xl border ${errors.Module_Description ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-blue-500"} focus:outline-none focus:ring-2 transition-all duration-200 resize-none`}
                                        placeholder="All subscription features will be here"
                                    />
                                    {errors.Module_Description && (
                                        <p className="mt-1.5 text-sm text-red-600">{errors.Module_Description.message}</p>
                                    )}
                                </div>

                                {/* Status Toggle - FIXED */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        Module Status <span className="text-red-500">*</span>
                                    </label>
                                    <Controller
                                        name="Module_Status_Id"
                                        control={control}
                                        rules={{ required: "Status is required" }}
                                        render={({ field }) => (
                                            <div className="flex items-center gap-4">
                                                <button
                                                    type="button"
                                                    onClick={() => field.onChange(field.value === "A" ? "I" : "A")}
                                                    className={`relative inline-flex items-center h-8 rounded-full w-16 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${field.value === "A" ? "bg-emerald-500" : "bg-gray-300"}`}
                                                >
                                                    <span
                                                        className={`inline-block w-6 h-6 transform transition-transform duration-300 bg-white rounded-full shadow-md ${field.value === "A" ? "translate-x-9" : "translate-x-1"}`}
                                                    />
                                                </button>
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-sm font-semibold ${field.value === "A" ? "text-emerald-600" : "text-gray-500"}`}>
                                                        {field.value === "A" ? "Active" : "Inactive"}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    />
                                    {errors.Module_Status_Id && (
                                        <p className="mt-1.5 text-sm text-red-600">{errors.Module_Status_Id.message}</p>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        disabled={isSubmitting}
                                        className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-6 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all duration-200 shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Saving...
                                            </>
                                        ) : (
                                            editableModule?.module_id ? "Update Module" : "Save Module"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AddNewModulePage;